import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";

import {
  buildHeaders,
  getBatteryDetails,
  getInverterDetails,
  getModuleDetails,
} from "../../services/api";
import {
  buildSldPayload,
  DEFAULT_SLD_OPTIONS,
  hasConfiguredBess,
  parseSldResponseMetadata,
  supportedBessTopology,
} from "./sldPayload";
import {
  PanelShell,
  PanelShellActions,
  PanelShellContent,
  PanelShellTitle,
} from "../PanelShell";

function downloadBlobUrl(blobUrl, filename) {
  if (!blobUrl) return;
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

function severityFor(message) {
  if (message?.severity === "error") return "error";
  if (message?.severity === "info") return "info";
  return "warning";
}

function BreakerOptions({ label, prefix, options, setOptions }) {
  const modeKey = `${prefix}_breaker_mode`;
  const ratingKey = `${prefix}_breaker_rating`;
  const voltageKey = `${prefix}_breaker_voltage`;
  const typeKey = `${prefix}_breaker_type`;
  const manual = options[modeKey] === "override";
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{label}</Typography>
      <RadioGroup
        row
        value={options[modeKey]}
        onChange={(event) => setOptions((previous) => ({ ...previous, [modeKey]: event.target.value }))}
      >
        <FormControlLabel value="auto" control={<Radio size="small" />} label="Use catalog/calculated" />
        <FormControlLabel value="override" control={<Radio size="small" />} label="Override manually" />
      </RadioGroup>
      <Grid container spacing={1}>
        <Grid size={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Rating (A)"
            value={options[ratingKey]}
            disabled={!manual}
            onChange={(event) => setOptions((previous) => ({ ...previous, [ratingKey]: event.target.value }))}
            helperText={manual ? "User override" : "Resolved by generator"}
          />
        </Grid>
        <Grid size={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Voltage (V)"
            value={options[voltageKey]}
            onChange={(event) => setOptions((previous) => ({ ...previous, [voltageKey]: event.target.value }))}
          />
        </Grid>
        <Grid size={4}>
          <TextField
            fullWidth
            select
            size="small"
            label="Type"
            value={options[typeKey]}
            onChange={(event) => setOptions((previous) => ({ ...previous, [typeKey]: event.target.value }))}
          >
            <MenuItem value="MCB">MCB</MenuItem>
            <MenuItem value="MCCB">MCCB</MenuItem>
          </TextField>
        </Grid>
      </Grid>
    </Box>
  );
}

// ../PanelShell: one level up, this file lives in components/sld/.
export default function SldPreviewDialog({ reportLanguage = "en", reportBranding, inline = false, open, onClose, config, currentProject, accessToken }) {
  const [options, setOptions] = useState(() => ({ ...DEFAULT_SLD_OPTIONS }));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [metadata, setMetadata] = useState({ warnings: [], pageCount: null, fingerprint: "" });
  const [regeneration, setRegeneration] = useState(0);
  const objectUrlRef = useRef("");
  const requestRef = useRef(0);
  const abortRef = useRef(null);

  const bessConfigured = hasConfiguredBess(config);
  const bessSupported = supportedBessTopology(config);
  const dcCoupled = String(config?.bess?.topology || config?.bess?.sldTopology || "")
    .trim().toLowerCase().replaceAll("_", "-").replaceAll(" ", "-") === "dc-coupled";
  const activeBatteryName = useMemo(() => {
    const selected = Array.isArray(config?.bess?.selected) ? config.bess.selected : [];
    return config?.bess?.activeName || selected[0]?.name || "";
  }, [config?.bess?.activeName, config?.bess?.selected]);

  const revokePdfUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = "";
    }
    setPdfUrl("");
  }, []);

  const closeDialog = useCallback(() => {
    abortRef.current?.abort();
    revokePdfUrl();
    setError("");
    setMetadata({ warnings: [], pageCount: null, fingerprint: "" });
    onClose?.();
  }, [onClose, revokePdfUrl]);

  useEffect(() => () => {
    abortRef.current?.abort();
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  useEffect(() => {
    if (!open) return;
    setOptions({
      ...DEFAULT_SLD_OPTIONS,
      project_title: config?.clientInfo?.project_title || currentProject?.name || config?.projectName || "",
      released_for: config?.metaInfo?.design_status || "PRELIMINARY",
      designed_by: config?.engInfo?.eng_name || "METEN",
      checked_by: config?.metaInfo?.study_approvedBy || "TBD",
    });
  }, [open, config?.clientInfo?.project_title, config?.engInfo?.eng_name, config?.metaInfo?.design_status, config?.metaInfo?.study_approvedBy, config?.projectName, currentProject?.name]);

  useEffect(() => {
    if (!bessSupported && options.include_bess) {
      setOptions((previous) => ({ ...previous, include_bess: false }));
    }
  }, [bessSupported, options.include_bess]);

  const generate = useCallback(async () => {
    if (!open) return;
    const baseUrl = process.env.REACT_APP_API_BASE?.replace(/\/$/, "");
    if (!baseUrl) {
      setError("REACT_APP_API_BASE is not set.");
      return;
    }
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError("");
    try {
      const [moduleDetails, inverterDetails, batteryDetails] = await Promise.all([
        getModuleDetails(config?.read_module, accessToken),
        getInverterDetails(config?.read_inverter, accessToken),
        options.include_bess && activeBatteryName
          ? getBatteryDetails(activeBatteryName, accessToken)
          : Promise.resolve(null),
      ]);
      if (controller.signal.aborted || requestId !== requestRef.current) return;
      const payload = buildSldPayload({
        config,
        currentProject,
        moduleDetails,
        inverterDetails,
        batteryDetails,
        options,
      });
      const response = await fetch(`${baseUrl}/pv/sld.pdf`, {
        method: "POST",
        headers: await buildHeaders(accessToken, { "Content-Type": "application/json" }),
        body: JSON.stringify({ ...payload, reportBranding, reportLanguage }),
        signal: controller.signal,
      });
      if (!response.ok) {
        let message = `HTTP ${response.status}`;
        try {
          const body = await response.json();
          message = body?.detail || message;
        } catch {}
        throw new Error(message);
      }
      const nextMetadata = parseSldResponseMetadata(response.headers);
      const blob = await response.blob();
      if (controller.signal.aborted || requestId !== requestRef.current) return;
      const nextUrl = URL.createObjectURL(blob);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = nextUrl;
      setPdfUrl(nextUrl);
      setMetadata(nextMetadata);
    } catch (caught) {
      if (caught?.name !== "AbortError" && requestId === requestRef.current) {
        setError(caught?.message || "Failed to generate SLD preview.");
      }
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [accessToken, activeBatteryName, config, currentProject, open, options, reportBranding, reportLanguage]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(generate, 450);
    return () => window.clearTimeout(timer);
  }, [generate, open, regeneration]);

  const warningCount = metadata.warnings.length;
  const fileName = `${currentProject?.name || config?.projectName || "PV Project"} - Single-Line Diagram.pdf`;
  const viewerUrl = pdfUrl ? `${pdfUrl}#page=1&view=FitH&toolbar=1&navpanes=0` : "";

  return (
    <PanelShell inline={inline} open={open} onClose={closeDialog} maxWidth={false} PaperProps={{ sx: { width: "96vw", height: "94vh", maxWidth: "none" } }}>
      <PanelShellTitle inline={inline} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Box>
          <Typography variant="h6">PV Single-Line Diagram</Typography>
          <Typography variant="caption" color="text.secondary">
            Generic A1 multi-sheet drawing generated from the saved electrical design and selected catalogs.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {metadata.pageCount ? <Chip size="small" label={`${metadata.pageCount} sheets`} /> : null}
          <Chip size="small" color={warningCount ? "warning" : "success"} label={`${warningCount} validation message${warningCount === 1 ? "" : "s"}`} />
        </Stack>
      </PanelShellTitle>
      {/* Inline the diagram needs a real height: the dialog gave it 94vh and a
          panel would otherwise collapse to its content, which for an <object>
          embed is zero. */}
      <PanelShellContent inline={inline} dividers sx={{ p: 1.5, minHeight: 0, ...(inline ? { height: "76vh" } : {}) }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "360px minmax(0, 1fr)" }, gap: 1.5, height: "100%" }}>
          <Paper variant="outlined" sx={{ p: 1.5, overflow: "auto", minHeight: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Drawing options</Typography>
            <Stack>
              <FormControlLabel control={<Switch checked={options.use_generation_meter} onChange={(event) => setOptions((previous) => ({ ...previous, use_generation_meter: event.target.checked }))} />} label="Generation meter" />
              <FormControlLabel control={<Switch checked={options.use_spd} onChange={(event) => setOptions((previous) => ({ ...previous, use_spd: event.target.checked }))} />} label="AC surge protection device" />
              <FormControlLabel control={<Switch checked={options.use_nvr} onChange={(event) => setOptions((previous) => ({ ...previous, use_nvr: event.target.checked }))} />} label="No-voltage relay" />
              <FormControlLabel
                control={<Switch checked={options.include_bess} disabled={!bessConfigured || !bessSupported} onChange={(event) => setOptions((previous) => ({ ...previous, include_bess: event.target.checked }))} />}
                label="Include BESS in SLD"
              />
              {!bessConfigured ? <FormHelperText>No BESS is configured for this project.</FormHelperText> : null}
              {bessConfigured && !bessSupported ? <Alert severity="warning" sx={{ mt: 0.5 }}>{dcCoupled ? "A DC-coupled SLD requires exactly one saved PV inverter. This project cannot draw a shared inverter with multiple PV inverters." : "Select AC-coupled or DC-coupled battery topology before adding BESS to the SLD."}</Alert> : null}
              {bessConfigured && bessSupported && dcCoupled ? <FormHelperText>The DC battery branch is shown on the shared hybrid inverter. DC protection, cable and equipment compatibility require final datasheet verification.</FormHelperText> : null}
            </Stack>

            {options.use_generation_meter ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Generation meter settings</Typography>
                <Grid container spacing={1} sx={{ mt: 0 }}>
                  <Grid size={6}><TextField fullWidth size="small" type="number" label="Accuracy class" value={options.meter_accuracy_class} onChange={(event) => setOptions((previous) => ({ ...previous, meter_accuracy_class: event.target.value }))} /></Grid>
                  <Grid size={6}><TextField fullWidth size="small" type="number" label="CT primary (A)" value={options.meter_primary_current} onChange={(event) => setOptions((previous) => ({ ...previous, meter_primary_current: event.target.value }))} helperText="TBD if blank" /></Grid>
                  <Grid size={6}><TextField fullWidth size="small" type="number" label="CT secondary (A)" value={options.meter_secondary_current} onChange={(event) => setOptions((previous) => ({ ...previous, meter_secondary_current: event.target.value }))} /></Grid>
                  <Grid size={6}><TextField fullWidth size="small" type="number" label="Burden (VA)" value={options.meter_burden_rating} onChange={(event) => setOptions((previous) => ({ ...previous, meter_burden_rating: event.target.value }))} /></Grid>
                </Grid>
              </Box>
            ) : null}

            {options.use_spd ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>AC SPD settings</Typography>
                <Grid container spacing={1} sx={{ mt: 0 }}>
                  <Grid size={4}><TextField fullWidth size="small" type="number" label="Voltage (V)" value={options.spd_voltage} onChange={(event) => setOptions((previous) => ({ ...previous, spd_voltage: event.target.value }))} /></Grid>
                  <Grid size={4}><TextField fullWidth size="small" type="number" label="Capacity (kA)" value={options.spd_current_capacity} onChange={(event) => setOptions((previous) => ({ ...previous, spd_current_capacity: event.target.value }))} /></Grid>
                  <Grid size={4}><TextField fullWidth size="small" type="number" label="Type" value={options.spd_type} onChange={(event) => setOptions((previous) => ({ ...previous, spd_type: event.target.value }))} /></Grid>
                </Grid>
              </Box>
            ) : null}

            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>Main contactor</Typography>
              <Grid container spacing={1} sx={{ mt: 0 }}>
                <Grid size={7}><TextField fullWidth size="small" label="Label" value={options.contactor_label} onChange={(event) => setOptions((previous) => ({ ...previous, contactor_label: event.target.value }))} /></Grid>
                <Grid size={5}><TextField fullWidth size="small" type="number" label="Rating (A)" value={options.contactor_rating} onChange={(event) => setOptions((previous) => ({ ...previous, contactor_rating: event.target.value }))} helperText="TBD if blank" /></Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 1.5 }} />
            <BreakerOptions label="Main AC protection" prefix="main" options={options} setOptions={setOptions} />
            <Divider sx={{ my: 1.5 }} />
            <BreakerOptions label="Inverter feeder protection" prefix="inverter" options={options} setOptions={setOptions} />

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Cable labels</Typography>
            <Stack spacing={1}>
              <TextField size="small" label="Inverter feeder cable" value={options.feeder_cable_label} onChange={(event) => setOptions((previous) => ({ ...previous, feeder_cable_label: event.target.value }))} helperText="User-entered preliminary value or TBD" />
              <TextField size="small" label="Main cable to client panel / PCC" value={options.client_panel_cable_label} onChange={(event) => setOptions((previous) => ({ ...previous, client_panel_cable_label: event.target.value }))} helperText="User-entered preliminary value or TBD" />
              <TextField size="small" label="DC string cable" value={options.dc_cable_label} onChange={(event) => setOptions((previous) => ({ ...previous, dc_cable_label: event.target.value }))} helperText="User-entered preliminary value or TBD" />
            </Stack>

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Title block</Typography>
            <Stack spacing={1}>
              <TextField size="small" label="Design status" value={options.released_for} onChange={(event) => setOptions((previous) => ({ ...previous, released_for: event.target.value }))} />
              <TextField size="small" label="Project title" value={options.project_title} onChange={(event) => setOptions((previous) => ({ ...previous, project_title: event.target.value }))} />
              <Grid container spacing={1}>
                <Grid size={6}><TextField fullWidth size="small" label="Prepared by" value={options.designed_by} onChange={(event) => setOptions((previous) => ({ ...previous, designed_by: event.target.value }))} /></Grid>
                <Grid size={6}><TextField fullWidth size="small" label="Checked by" value={options.checked_by} onChange={(event) => setOptions((previous) => ({ ...previous, checked_by: event.target.value }))} /></Grid>
                <Grid size={6}><TextField fullWidth size="small" label="Drawn by" value={options.drawn_by} onChange={(event) => setOptions((previous) => ({ ...previous, drawn_by: event.target.value }))} /></Grid>
                <Grid size={6}><TextField fullWidth size="small" label="Approved by" value={options.approved_by} onChange={(event) => setOptions((previous) => ({ ...previous, approved_by: event.target.value }))} /></Grid>
              </Grid>
            </Stack>

            {warningCount ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>Validation</Typography>
                <Stack spacing={0.75}>
                  {metadata.warnings.map((warning, index) => (
                    <Alert severity={severityFor(warning)} key={`${warning.code || "warning"}-${index}`} sx={{ py: 0.25 }}>
                      <Typography variant="caption"><strong>{warning.code || "Review"}:</strong> {warning.message}</Typography>
                    </Alert>
                  ))}
                </Stack>
              </Box>
            ) : null}
          </Paper>

          <Paper variant="outlined" sx={{ minWidth: 0, minHeight: 0, overflow: "hidden", bgcolor: "#2f3035", display: "grid", placeItems: "stretch" }}>
            {loading ? (
              <Box sx={{ display: "grid", placeItems: "center", color: "common.white" }}>
                <Stack alignItems="center" spacing={1}><CircularProgress size={28} /><Typography variant="body2">Generating and validating all sheets…</Typography></Stack>
              </Box>
            ) : error ? (
              <Box sx={{ p: 2 }}><Alert severity="error">{error}</Alert></Box>
            ) : viewerUrl ? (
              <iframe title="SLD PDF preview" src={viewerUrl} style={{ width: "100%", height: "100%", minHeight: "72vh", border: 0, display: "block" }} />
            ) : (
              <Box sx={{ display: "grid", placeItems: "center", color: "grey.300" }}><Typography>No SLD has been generated.</Typography></Box>
            )}
          </Paper>
        </Box>
      </PanelShellContent>
      <PanelShellActions inline={inline} sx={{ justifyContent: "space-between" }}>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} disabled={loading} onClick={() => setRegeneration((value) => value + 1)}>Regenerate</Button>
          <Button variant="contained" startIcon={<DownloadIcon />} disabled={!pdfUrl || loading} onClick={() => downloadBlobUrl(pdfUrl, fileName)}>Download same PDF</Button>
        </Stack>
        {!inline && <Button onClick={closeDialog}>Close</Button>}
      </PanelShellActions>
    </PanelShell>
  );
}
